import type { BullionRoom } from "./types";

export type RawRunner = {
    id?: string | number;
    name?: string | unknown;
    odds?: number | string;
    recommended_price?: number | string;
    status?: string | number;
    winner?: boolean | number | string;
    image?: string | number;
    Image?: string | number;
    photo?: string | number;
    image_url?: string | number;
    imageUrl?: string | number;
    "selection-name"?: string;
    "participant-name"?: string;
    line?: string | number;
    Line?: string | number;
    handicap?: string | number;
    hcp?: string | number;
    points?: string | number;
};

export type RawMarket = {
    id?: string | number;
    market_id?: string | number;
    market_line_id?: string | number;
    market_type_id?: string | number;
    name?: string;
    market_name?: string;
    market_type?: string;
    status?: string | number;
    active?: string | number | boolean;
    line?: string | number;
    Line?: string | number;
    handicap?: string | number;
    hcp?: string | number;
    total?: string | number;
    points?: string | number;
    runners?: RawRunner[];
    selections?: RawRunner[];
    selectionslist?: RawRunner[];
};

export type RawMatch = {
    id?: string | number;
    name?: string;
    sport?: string;
    start?: string;
    actual_start?: string;
    competition?: string;
    status?: string;
    liveScore?: { isLive?: boolean; isFinished?: boolean };
    format?: number;
    markets?: RawMarket[];
};

/** Fixture has reached a terminal state on the cricket feed (no longer "in-play" for lobby purposes). */
export function isRawMatchFinishedForBullionLobby(match: RawMatch): boolean {
    const status = String(match.status || "").toUpperCase();
    if (
        status.includes("MATCH COMPLETE") ||
        status.includes("FINISHED") ||
        status.includes("FULL TIME") ||
        status.includes("EVENT CLOSED") ||
        status === "EVENT_FULL_RESULT" ||
        status.includes("ABANDONED")
    ) {
        return true;
    }
    const ls = match.liveScore;
    if (ls && typeof ls === "object" && (ls as { isFinished?: boolean }).isFinished === true) return true;

    const sport = String(match.sport || "").toLowerCase();
    if (sport === "cricket") {
        const startRaw = match.start ?? match.actual_start;
        if (startRaw) {
            const startMs = new Date(String(startRaw)).getTime();
            if (Number.isFinite(startMs) && startMs <= Date.now()) {
                const st = String(match.status || "").toUpperCase();
                const stillScheduled =
                    st.includes("MATCH SCHEDULED") || st === "" || !String(match.status || "").trim();
                const feedLive =
                    ls && typeof ls === "object" && (ls as { isLive?: boolean }).isLive === true;
                if (stillScheduled && !feedLive) {
                    const hoursSinceKickoff = (Date.now() - startMs) / (1000 * 60 * 60);
                    if (hoursSinceKickoff >= 4) return true;
                }
            }
        }
    }
    return false;
}

/** Stamp terminal feed fields onto a lobby raw row (session cache / landing upsert). */
export function stampFinishedOntoRawMatchRow(match: RawMatch): RawMatch {
    const prevLs =
        match.liveScore && typeof match.liveScore === "object"
            ? (match.liveScore as { isLive?: boolean; isFinished?: boolean })
            : {};
    return {
        ...match,
        status: "MATCH COMPLETE",
        liveScore: { ...prevLs, isFinished: true, isLive: false },
    };
}

/** Whether BULLION should treat the fixture as "live" for lobby purposes. */
export function isRawMatchLiveForBullionLobby(match: RawMatch): boolean {
    if (isRawMatchFinishedForBullionLobby(match)) return false;

    const ls = match.liveScore;
    if (ls && typeof ls === "object") {
        if (ls.isFinished === true) return false;
        if (ls.isLive === true) return true;
    }

    const status = String(match.status || "").toUpperCase();
    if (
        status.includes("MATCH IN PROGRESS") ||
        status.includes("IN-PLAY") ||
        status.includes("IN PLAY") ||
        status.includes("LIVE") ||
        status.includes("INNINGS BREAK") ||
        status.includes("BREAK IN PLAY") ||
        status.includes("TEA") ||
        status.includes("LUNCH") ||
        status.includes("DRINKS") ||
        status.includes("INNINGS COMPLETE") ||
        // Chase target ("Play to 150") — not pre-match "Play to Start Shortly"
        /\bPLAY TO \d/.test(status)
    ) {
        return true;
    }

    const sport = String(match.sport || "").toLowerCase();
    const effectiveStart = match.actual_start ?? match.start;
    if (sport === "cricket" && effectiveStart) {
        const st = String(match.status || "").toUpperCase();
        const stillScheduled =
            st.includes("MATCH SCHEDULED") || st === "" || !String(match.status || "").trim();
        if (!stillScheduled) {
            const startMs = new Date(effectiveStart).getTime();
            if (!Number.isNaN(startMs)) {
                const minutesSinceStart = (Date.now() - startMs) / (1000 * 60);
                if (minutesSinceStart >= 0 && minutesSinceStart < 720) return true;
            }
        }
    }

    return false;
}

export type BullionLobbyFixturePhase = "scheduled" | "live" | "finished";

/** Pre-start vs in-play vs finished for Bullion lobby / match landing labels. */
export function bullionLobbyFixturePhase(match: RawMatch): BullionLobbyFixturePhase {
    if (isRawMatchFinishedForBullionLobby(match)) return "finished";
    if (isRawMatchLiveForBullionLobby(match)) return "live";
    return "scheduled";
}

/** `BullionRoom` built from lobby data or recovery — same phase labels when `fixturePhase` was omitted. */
export function bullionRoomResolvedFixturePhase(
    room: Pick<BullionRoom, "fixturePhase" | "status" | "isLive">
): BullionLobbyFixturePhase {
    if (room.fixturePhase) return room.fixturePhase;
    const st = String(room.status || "").toUpperCase();
    if (
        st.includes("MATCH COMPLETE") ||
        st.includes("FINISHED") ||
        st.includes("FULL TIME") ||
        st.includes("EVENT CLOSED") ||
        st === "EVENT_FULL_RESULT" ||
        st.includes("ABANDONED")
    ) {
        return "finished";
    }
    return room.isLive ? "live" : "scheduled";
}
